import { StyledLink } from '@/components/common/styledLink';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { formatNumberToCurrency } from '@/utils/currency';
import {
  AccountCategory,
  BalanceDataInterface,
} from '@/interfaces/balanceSheet';
import { mobileWidth } from '@/constants';
import { convertNumberToPercentage } from '@/utils/numbers';
import { BalanceSectionHeader } from '@/modules/balanceSheet/components/BalanceSectionHeader';
import { useCallback, useMemo, useState } from 'react';
import {
  DataGridPremium,
  GridColumnVisibilityModel,
  GridColDef,
  GridListViewColDef,
  GridRenderCellParams,
  type GridToolbarProps,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import BalanceSheetSkeleton from './BalanceSheetSkeleton';
import { useDrilldown } from '@/hooks/useDrilldown';
import { DrilldownView } from '@/components/views/DrilldownView';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { GridContainer } from '../../components/grids/GridContainer';

/** A $0 section total would make the share 0/0; render those categories as 0%. */
const ratioOfTotal = (value: number, total: number): number =>
  total === 0 ? 0 : value / total;

interface BalanceSheetRow {
  id: string;
  name: string;
  percentageOfTotal: number;
  total: number;
  isTotal?: boolean;
}

interface BalanceSheetGridProps {
  balanceData: BalanceDataInterface;
  calculatedTotalAssets: number;
  dateFilter: DataRangeFilterEnum;
  isLoading?: boolean;
  /**
   * Advisor Balance Sheet page (IU-64): pin a column-total row at the bottom.
   */
  advisorView?: boolean;
  /**
   * Stacks assets above liabilities regardless of viewport width, for the
   * half-width widget slot where the two columns would otherwise be cramped.
   */
  stacked?: boolean;
  /** When provided, a category click bubbles up instead of drilling inline (IU-31). */
  onDrilldown?: (category: AccountCategory) => void;
  /** Drill-down accounts grid fills the page height. Off inside a widget. */
  fillDrilldownHeight?: boolean;
}

export default function BalanceSheetGrid({
  balanceData,
  calculatedTotalAssets,
  isLoading = false,
  advisorView = false,
  stacked = false,
  onDrilldown,
  fillDrilldownHeight = false,
}: BalanceSheetGridProps) {
  const { assets, liabilities } = balanceData;
  const isMobileMatch = useMediaQuery(mobileWidth);
  // Drives both the vertical layout and the grids' narrow mode: a half width
  // or embedded tile can be 380px wide on a desktop viewport, where a desktop
  // three column table clips its headers and currency values.
  const stackSections = isMobileMatch || stacked;
  const authLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const { drilledDownCategory, handleDrilldown, handleBackClick } =
    useDrilldown();

  const handleCategoryClick = useCallback(
    (categoryName: string, isLiability: boolean) => {
      const categories = isLiability
        ? liabilities.categories
        : assets.categories;
      const category = categories.find((c) => c.name === categoryName);
      if (category) {
        (onDrilldown ?? handleDrilldown)(category);
      }
    },
    [assets.categories, handleDrilldown, liabilities.categories, onDrilldown],
  );

  // Capacitor.getPlatform can be absent in non-native web builds; keep the guard.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';

  const renderNameCell = useCallback(
    (params: GridRenderCellParams<BalanceSheetRow>, isLiability: boolean) => {
      if (params.row.isTotal) {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {params.row.name}
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => {
            handleCategoryClick(params.row.name, isLiability);
          }}
        >
          <StyledLink
            text={params.row.name}
            showUnderline={false}
          />
        </Box>
      );
    },
    [handleCategoryClick],
  );

  const [assetsColumnVisibilityModel, setAssetsColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [
    liabilitiesColumnVisibilityModel,
    setLiabilitiesColumnVisibilityModel,
  ] = useState<GridColumnVisibilityModel>({});

  const assetColumns: GridColDef<BalanceSheetRow>[] = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('grid.type'),
        renderCell: (params: GridRenderCellParams<BalanceSheetRow>) =>
          renderNameCell(params, false),
        flex: 1,
      },
      {
        field: 'percentageOfTotal',
        headerName: t('grid.percentOfTotal'),
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value?: number) =>
          convertNumberToPercentage(value || 0),
        flex: 1,
      },
      {
        field: 'total',
        headerName: t('grid.balance'),
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value?: number) => formatNumberToCurrency(value || 0),
        flex: 1,
      },
    ];
  }, [renderNameCell, t]);

  const liabilityColumns: GridColDef<BalanceSheetRow>[] = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('grid.type'),
        renderCell: (params: GridRenderCellParams<BalanceSheetRow>) =>
          renderNameCell(params, true),
        flex: 1,
      },
      {
        field: 'percentageOfTotal',
        headerName: t('grid.percentOfTotal'),
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value?: number) =>
          convertNumberToPercentage(value || 0),
        flex: 1,
      },
      {
        field: 'total',
        headerName: t('grid.balance'),
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value?: number) => formatNumberToCurrency(value || 0),
        flex: 1,
      },
    ];
  }, [renderNameCell, t]);

  const createListViewColDef = useMemo(
    () =>
      (isLiability: boolean): GridListViewColDef<BalanceSheetRow> => ({
        field: 'listColumn',
        renderCell: (params) => {
          const visibilityModel = params.api.state.columns
            .columnVisibilityModel as Record<string, boolean | undefined>;
          const isFieldVisible = (field: keyof BalanceSheetRow) =>
            visibilityModel[field] !== false;

          const showType = isFieldVisible('name');
          const showBalance = isFieldVisible('total');
          const showPercent = isFieldVisible('percentageOfTotal');

          const lineOneLeft = showType ? params.row.name : t('grid.type');
          const lineOneRight = showBalance
            ? formatNumberToCurrency(params.row.total || 0)
            : '';

          return (
            <Box sx={{ width: '100%', py: 2, px: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleCategoryClick(params.row.name, isLiability);
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, px: 0 }}>
                  <Typography
                    variant='body2'
                    color='text.primary'
                    sx={{ fontWeight: '600 !important' }}
                  >
                    {lineOneLeft}
                  </Typography>
                </Box>

                <Typography
                  variant='body2'
                  color='text.primary'
                  sx={{
                    fontWeight: '600 !important',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lineOneRight}
                </Typography>
              </Box>

              {showPercent ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 0.25,
                    fontSize: '14px',
                  }}
                >
                  <Typography color='text.secondary'>
                    {convertNumberToPercentage(
                      params.row.percentageOfTotal || 0,
                    )}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          );
        },
      }),
    [t, handleCategoryClick],
  );

  const assetsListViewColDef = useMemo(
    () => createListViewColDef(false),
    [createListViewColDef],
  );
  const liabilitiesListViewColDef = useMemo(
    () => createListViewColDef(true),
    [createListViewColDef],
  );

  if (!authLoaded || isLoading) {
    return <BalanceSheetSkeleton stacked={stackSections} />;
  }

  const assetsRows: BalanceSheetRow[] = assets.categories.map(
    (category, index) => ({
      id: `asset-${String(index)}`,
      name: category.name,
      percentageOfTotal: ratioOfTotal(category.total, calculatedTotalAssets),
      total: category.total,
    }),
  );

  const liabilitiesRows: BalanceSheetRow[] = liabilities.categories.map(
    (category, index) => ({
      id: `liability-${String(index)}`,
      name: category.name,
      percentageOfTotal: ratioOfTotal(category.total, liabilities.total),
      total: category.total,
    }),
  );

  // IU-64: pin a column-total row at the bottom of each grid on the advisor page.
  // IU-293: on mobile the totals belong at the top instead, where the page
  // summary and each section header already show them — so don't pin them.
  // IU-245: stacked sections read like the mobile layout, so the section
  // headers carry the totals there too.
  const showPinnedTotals = advisorView && !stackSections;
  const assetsPinnedRows = showPinnedTotals
    ? {
        bottom: [
          {
            id: 'assets-total',
            name: t('balanceSheetPage.totalAssets', 'Total Assets'),
            // Same guard the category rows use: a $0 section is 0%, not 100%.
            percentageOfTotal: ratioOfTotal(
              assets.total,
              calculatedTotalAssets,
            ),
            total: assets.total,
            isTotal: true,
          },
        ],
      }
    : undefined;
  const liabilitiesPinnedRows = showPinnedTotals
    ? {
        bottom: [
          {
            id: 'liabilities-total',
            name: t('balanceSheetPage.totalLiabilities', 'Total Liabilities'),
            percentageOfTotal: ratioOfTotal(
              liabilities.total,
              liabilities.total,
            ),
            total: liabilities.total,
            isTotal: true,
          },
        ],
      }
    : undefined;

  return (
    <DrilldownView
      category={drilledDownCategory}
      fillHeight={fillDrilldownHeight}
      onBack={handleBackClick}
      groupBySubcategory
    >
      <Box
        display='flex'
        flexDirection={stackSections ? 'column' : 'row'}
        justifyContent='space-between'
        gap={5}
      >
        {/* Assets */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BalanceSectionHeader
            title={t('assets')}
            amount={assets.total}
          />
          <GridContainer
            isWidget={false}
            size='auto'
          >
            <DataGridPremium<BalanceSheetRow>
              rows={assetsRows}
              columns={assetColumns}
              pinnedRows={assetsPinnedRows}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'total', sort: 'desc' }],
                },
              }}
              disableRowSelectionOnClick
              hideFooter
              autoHeight
              showToolbar
              disableColumnMenu
              disableColumnSelector
              disablePivoting
              listView={stackSections}
              listViewColumn={assetsListViewColDef}
              getRowHeight={() => (stackSections ? 'auto' : null)}
              columnVisibilityModel={assetsColumnVisibilityModel}
              onColumnVisibilityModelChange={setAssetsColumnVisibilityModel}
              slotProps={{
                ...(stackSections
                  ? { toolbar: { showQuickFilter: true } }
                  : {
                      toolbar: {
                        ...defaultToolbarExportSlotProps,
                        showQuickFilter: true,
                      },
                    }),
              }}
              slots={{
                columnMenu: () => null,
                ...(isNative
                  ? {
                      toolbar: (props: GridToolbarProps) => (
                        <CustomToolbarForNative
                          showColumns={false}
                          {...props}
                        />
                      ),
                    }
                  : stackSections
                    ? {
                        toolbar: () => (
                          <XsPreferencesDrawerToolbar
                            showColumns={false}
                            showQuickFilter
                          />
                        ),
                      }
                    : {}),
              }}
              sx={[
                advisorView && {
                  '& .MuiDataGrid-pinnedRows .MuiDataGrid-cell': {
                    fontWeight: 'bold',
                    color: 'success.main',
                  },
                },
                stackSections && {
                  '& .MuiDataGrid-cell': {
                    width: '100%',
                    minWidth: '100%',
                    px: 0,
                  },
                  '& .MuiDataGrid-toolbar': {
                    px: 0,
                    alignItems: 'flex-end',
                  },
                },
              ]}
            />
          </GridContainer>
        </Box>

        {/* Liabilities */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BalanceSectionHeader
            title={t('liabilities')}
            amount={liabilities.total}
            isNegative={true}
          />
          <GridContainer
            isWidget={false}
            size='auto'
          >
            <DataGridPremium<BalanceSheetRow>
              rows={liabilitiesRows}
              columns={liabilityColumns}
              pinnedRows={liabilitiesPinnedRows}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'total', sort: 'asc' }],
                },
              }}
              disableRowSelectionOnClick
              hideFooter
              autoHeight
              showToolbar
              disablePivoting
              disableColumnMenu
              disableColumnSelector
              listView={stackSections}
              listViewColumn={liabilitiesListViewColDef}
              getRowHeight={() => (stackSections ? 'auto' : null)}
              columnVisibilityModel={liabilitiesColumnVisibilityModel}
              onColumnVisibilityModelChange={
                setLiabilitiesColumnVisibilityModel
              }
              slotProps={{
                ...(stackSections
                  ? { toolbar: { showQuickFilter: true } }
                  : {
                      toolbar: {
                        ...defaultToolbarExportSlotProps,
                        showQuickFilter: true,
                      },
                    }),
              }}
              slots={{
                columnMenu: () => null,
                ...(isNative
                  ? {
                      toolbar: (props: GridToolbarProps) => (
                        <CustomToolbarForNative
                          showColumns={false}
                          {...props}
                        />
                      ),
                    }
                  : stackSections
                    ? {
                        toolbar: () => (
                          <XsPreferencesDrawerToolbar
                            showColumns={false}
                            showQuickFilter
                          />
                        ),
                      }
                    : {}),
              }}
              sx={[
                advisorView && {
                  '& .MuiDataGrid-pinnedRows .MuiDataGrid-cell': {
                    fontWeight: 'bold',
                    color: 'error.main',
                  },
                },
                stackSections && {
                  '& .MuiDataGrid-cell': {
                    width: '100%',
                    minWidth: '100%',
                    px: 0,
                  },
                  '& .MuiDataGrid-toolbar': {
                    px: 0,
                    alignItems: 'flex-end',
                  },
                },
              ]}
            />
          </GridContainer>
        </Box>
      </Box>
    </DrilldownView>
  );
}
